import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CONTROL_REVIEW_CATEGORIES,
  CONTROL_REVIEW_CATEGORY_HINTS,
  CONTROL_REVIEW_CATEGORY_LABELS,
  CONTROL_REVIEW_CATEGORY_SOURCES,
  CONTROL_REVIEW_PRIORITIES,
  CONTROL_REVIEW_PRIORITY_HINTS,
  CONTROL_REVIEW_PRIORITY_LABELS,
  CONTROL_REVIEW_SOURCE_MODULE_LABELS,
  CONTROL_REVIEW_STATUSES,
  CONTROL_REVIEW_STATUS_LABELS,
  type ControlReviewCategory,
  type ControlReviewPriority,
  type ControlReviewSourceModule,
  type ControlReviewStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Field, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2G.9 — Operational Loss Prevention & Control Review.
 *
 * A control review case is an administrator-driven investigation record
 * around an existing source of truth. This page NEVER renders a verdict:
 *   - the evidence panel shows the immutable snapshot captured at opening
 *     (facts exactly as recorded, plus disclosed limitations),
 *   - the current-state panel is a LIVE comparison from the source module,
 *     clearly separated from the snapshot,
 *   - investigator notes and conclusions are visually distinct from facts
 *     (notes live in the attributed history, not in the evidence),
 *   - resolving a case changes only the case; every real correction must
 *     go through its own module's workflow (linked from the detail).
 */

interface CaseActor {
  id: string | null;
  name: string;
}

interface CaseDto {
  id: string;
  reference: string;
  branch: { id: string; code: string };
  category: ControlReviewCategory;
  categoryLabel: string;
  categoryHint: string;
  status: ControlReviewStatus;
  statusLabel: string;
  priority: ControlReviewPriority;
  priorityLabel: string;
  source: { module: ControlReviewSourceModule; moduleLabel: string; recordId: string; ref: string };
  title: string;
  description: string | null;
  createdBy: CaseActor | null;
  createdAt: string;
  assignedTo: CaseActor | null;
  correctiveAction: string | null;
  resolutionNotes: string | null;
  resolvedBy: CaseActor | null;
  resolvedAt: string | null;
  reopenReason: string | null;
  reopenedBy: CaseActor | null;
  reopenedAt: string | null;
  reopenCount: number;
}

interface EvidenceFact {
  label: string;
  value: string | number | boolean | null;
}

interface EvidenceSnapshot {
  category: ControlReviewCategory;
  sourceModule: ControlReviewSourceModule;
  sourceRecordId: string;
  sourceRef: string;
  branch: { id: string; code: string };
  capturedAt: string;
  reason: string;
  facts: EvidenceFact[];
  indicators: string[];
  limitations: string[];
}

interface CurrentState {
  available: boolean;
  summary: string;
  changedSinceCapture: boolean;
  changedFacts: { label: string; value: string }[];
}

interface ActivityDto {
  id: string;
  action: string;
  note: string | null;
  actor: CaseActor;
  actorRole: string;
  metadata: unknown;
  createdAt: string;
}

interface ListBody {
  total: number;
  limit: number;
  offset: number;
  items: CaseDto[];
  range: { from: string | null; to: string | null } | null;
  filters: { status: string | null; category: string | null; priority: string | null };
}

interface DetailBody {
  case: CaseDto;
  evidence: EvidenceSnapshot;
  current: CurrentState | null;
  history: ActivityDto[];
}

const PAGE_SIZE = 20;

const STATUS_TONE: Record<ControlReviewStatus, 'slate' | 'indigo' | 'amber' | 'emerald' | 'rose'> = {
  OPEN: 'slate',
  UNDER_REVIEW: 'indigo',
  ACTION_REQUIRED: 'amber',
  RESOLVED: 'emerald',
  REOPENED: 'rose',
};

const PRIORITY_TONE: Record<ControlReviewPriority, 'slate' | 'amber' | 'rose'> = {
  LOW: 'slate',
  MEDIUM: 'amber',
  HIGH: 'rose',
};

/** Deep links from a case to the module that owns its source record. */
const SOURCE_LINKS: Record<ControlReviewSourceModule, string> = {
  TRANSACTION: '/admin/monitoring',
  PROCESSING: '/admin/discrepancies',
  RECONCILIATION: '/admin/reconciliation',
  INVENTORY: '/admin/inventory',
  PURCHASE: '/admin/purchasing',
  EXPENSE: '/admin/expenses',
  HANDOVER: '/admin/handover',
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

/** History action -> human label (vocabulary reuse, no client-side rules). */
const HISTORY_LABELS: Record<string, string> = {
  'control_review.opened': 'Case opened',
  'control_review.started': 'Review started',
  'control_review.assigned': 'Reviewer assigned',
  'control_review.note_added': 'Investigation note',
  'control_review.action_requested': 'Corrective action recorded',
  'control_review.action_continued': 'Review continued',
  'control_review.resolved': 'Case resolved',
  'control_review.reopened': 'Case reopened',
};

function FactRow({ fact }: { fact: EvidenceFact }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-sm text-slate-500">{fact.label}</span>
      <span className="max-w-[60%] break-words text-right text-sm font-medium tabular-nums text-slate-800">
        {fact.value === null ? (
          <span className="italic text-slate-400">Not recorded</span>
        ) : typeof fact.value === 'boolean' ? (
          fact.value ? 'Yes' : 'No'
        ) : (
          fact.value
        )}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: ControlReviewStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{CONTROL_REVIEW_STATUS_LABELS[status]}</Badge>;
}

function PriorityBadge({ priority }: { priority: ControlReviewPriority }) {
  return <Badge tone={PRIORITY_TONE[priority]}>{CONTROL_REVIEW_PRIORITY_LABELS[priority]}</Badge>;
}

/* ---------------- open-case form state ---------------- */

interface OpenFormState {
  category: ControlReviewCategory;
  module: ControlReviewSourceModule;
  ref: string;
  title: string;
  description: string;
  initialNote: string;
}

export function ControlReviewPage() {
  const { branchFilter } = useAuth();
  // Deep links from the exception / discrepancy pages pre-fill the form
  // (?category=...&module=...&ref=...&title=...) - nothing is opened
  // automatically; the admin still reviews and confirms.
  const [searchParams] = useSearchParams();

  const [list, setList] = useState<ListBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one re-validated server-side.
  const [statusFilter, setStatusFilter] = useState<'' | ControlReviewStatus>('');
  const [categoryFilter, setCategoryFilter] = useState<'' | ControlReviewCategory>('');
  const [priorityFilter, setPriorityFilter] = useState<'' | ControlReviewPriority>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [qFilter, setQFilter] = useState('');
  const [page, setPage] = useState(0);

  // Detail modal.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailBody | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Note / corrective action / resolve / reopen dialogs.
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [actionOpen, setActionOpen] = useState(false);
  const [actionText, setActionText] = useState('');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveText, setResolveText] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenText, setReopenText] = useState('');

  // Open-case form.
  const [form, setForm] = useState<OpenFormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);

  // One-time deep-link prefill from the exception/discrepancy pages.
  useEffect(() => {
    const category = searchParams.get('category') as ControlReviewCategory | null;
    const module = searchParams.get('module') as ControlReviewSourceModule | null;
    const ref = searchParams.get('ref');
    if (!category || !module || !ref) return;
    if (!CONTROL_REVIEW_CATEGORIES.includes(category)) return;
    if (!CONTROL_REVIEW_CATEGORY_SOURCES[category].includes(module)) return;
    setForm({
      category,
      module,
      ref,
      title: searchParams.get('title') ?? '',
      description: '',
      initialNote: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopeParams = () => {
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);
    return params;
  };

  // List (paginated; server-side deterministic order).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = scopeParams();
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    if (priorityFilter) params.set('priority', priorityFilter);
    if (qFilter) params.set('q', qFilter);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));
    void api
      .get<ListBody>(`/control-reviews?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setList(res);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setList(null);
          setError(err instanceof ApiError ? err.message : 'Cannot load control review cases');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, statusFilter, categoryFilter, priorityFilter, fromFilter, toFilter, qFilter, page, refreshKey]);

  // Detail.
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailError(null);
    const params = branchFilter !== 'ALL' ? `?branch=${branchFilter}` : '';
    void api
      .get<DetailBody>(`/control-reviews/${detailId}${params}`)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetail(null);
          setDetailError(err instanceof ApiError ? err.message : 'Cannot load the case');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [detailId, branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const runAction = async (path: string, payload: unknown = {}): Promise<boolean> => {
    if (!detailId) return false;
    setActionBusy(true);
    setActionError(null);
    try {
      await api.post(`/control-reviews/${detailId}/${path}`, payload);
      refresh();
      return true;
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'The action could not be recorded');
      return false;
    } finally {
      setActionBusy(false);
    }
  };

  const submitOpen = async () => {
    if (!form) return;
    setFormBusy(true);
    setFormError(null);
    try {
      const body: Record<string, unknown> = {
        category: form.category,
        title: form.title,
        source:
          form.module === 'PROCESSING'
            ? { module: 'PROCESSING', stepId: form.ref.trim() }
            : form.module === 'RECONCILIATION' || form.module === 'INVENTORY'
              ? { module: form.module, id: form.ref.trim() }
              : { module: form.module, ref: form.ref.trim() },
      };
      if (form.description.trim()) body.description = form.description.trim();
      if (form.initialNote.trim()) body.initialNote = form.initialNote.trim();
      const res = await api.post<{ case: CaseDto }>('/control-reviews', body);
      setForm(null);
      setDetailId(res.case.id);
      refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'The case could not be opened');
    } finally {
      setFormBusy(false);
    }
  };

  const total = list?.total ?? 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  const filtersActive = Boolean(statusFilter || categoryFilter || priorityFilter || qFilter || fromFilter || toFilter);

  const liveCase = detail?.case;
  const canReview = liveCase && (liveCase.status === 'OPEN' || liveCase.status === 'REOPENED');
  const canAct = liveCase?.status === 'UNDER_REVIEW';
  const canContinue = liveCase?.status === 'ACTION_REQUIRED';
  const canResolve = liveCase?.status === 'UNDER_REVIEW' || liveCase?.status === 'ACTION_REQUIRED';
  const canReopen = liveCase?.status === 'RESOLVED';
  const canNote = liveCase && liveCase.status !== 'RESOLVED';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Control Review</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Investigation cases around recorded facts — an exception or a discrepancy is a reason to
            review the evidence, never a verdict. Opening or resolving a case never changes the
            underlying business record.
          </p>
        </div>
        <Button
          onClick={() =>
            setForm({
              category: 'ITEM_COUNT_DISCREPANCY',
              module: 'PROCESSING',
              ref: '',
              title: '',
              description: '',
              initialNote: '',
            })
          }
        >
          <Icon name="plus" className="h-4 w-4" />
          Open a case
        </Button>
      </header>

      {/* Filters */}
      {list && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(0);
              setStatusFilter(e.target.value as '' | ControlReviewStatus);
            }}
            aria-label="Filter by status"
            className="h-9 rounded-xl border border-slate-300 bg-panel px-2.5 text-sm font-medium text-slate-700"
          >
            <option value="">All statuses</option>
            {CONTROL_REVIEW_STATUSES.map((status) => (
              <option key={status} value={status}>
                {CONTROL_REVIEW_STATUS_LABELS[status]}
              </option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => {
              setPage(0);
              setCategoryFilter(e.target.value as '' | ControlReviewCategory);
            }}
            aria-label="Filter by category"
            className="h-9 rounded-xl border border-slate-300 bg-panel px-2.5 text-sm font-medium text-slate-700"
          >
            <option value="">All categories</option>
            {CONTROL_REVIEW_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {CONTROL_REVIEW_CATEGORY_LABELS[category]}
              </option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => {
              setPage(0);
              setPriorityFilter(e.target.value as '' | ControlReviewPriority);
            }}
            aria-label="Filter by priority"
            className="h-9 rounded-xl border border-slate-300 bg-panel px-2.5 text-sm font-medium text-slate-700"
          >
            <option value="">All priorities</option>
            {CONTROL_REVIEW_PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {CONTROL_REVIEW_PRIORITY_LABELS[priority]}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={fromFilter}
            onChange={(e) => {
              setPage(0);
              setFromFilter(e.target.value);
            }}
            aria-label="Opened from (Manila date)"
            className="h-9 rounded-xl border border-slate-300 bg-panel px-2.5 text-sm font-medium text-slate-700"
          />
          <input
            type="date"
            value={toFilter}
            onChange={(e) => {
              setPage(0);
              setToFilter(e.target.value);
            }}
            aria-label="Opened to (Manila date)"
            className="h-9 rounded-xl border border-slate-300 bg-panel px-2.5 text-sm font-medium text-slate-700"
          />

          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(0);
              setQFilter(searchInput.trim());
            }}
          >
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Case or source reference…"
              aria-label="Search cases"
              className="h-9 w-56 rounded-xl border border-slate-300 bg-panel px-3 text-sm text-slate-700 placeholder:text-slate-400"
            />
            <Button size="sm" variant="secondary" type="submit">
              Search
            </Button>
          </form>
        </div>
      )}

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading cases…
        </div>
      ) : !list || list.items.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon name="check" className="h-8 w-8 text-emerald-400" />
          <p className="mt-2 font-medium text-slate-700">
            {filtersActive ? 'No cases match these filters.' : 'No control review cases yet.'}
          </p>
          <p className="mt-1 max-w-md text-sm text-slate-500">
            {filtersActive
              ? 'Clear a filter to see the full picture.'
              : 'Open a case from an exception, a discrepancy or directly here when a record needs an investigation. A case documents the review — it never changes the record itself.'}
          </p>
        </Card>
      ) : (
        <>
          <p className="text-sm text-slate-500">
            Showing {from}–{to} of {total} {total === 1 ? 'case' : 'cases'}
            {list.range?.from || list.range?.to
              ? ` · opened ${list.range.from ?? '…'} to ${list.range.to ?? '…'} (${list.range && 'from' in list.range ? 'Manila dates' : ''})`
              : ''}
          </p>

          {/* Desktop table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Case</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Reviewer</th>
                  <th className="px-4 py-3">Opened</th>
                  <th className="px-4 py-3" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setDetailId(row.id)}
                    className="cursor-pointer hover:bg-slate-100/70"
                  >
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{row.reference}</p>
                      <p className="mt-0.5 max-w-56 truncate text-xs text-slate-500">{row.title}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{row.categoryLabel}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-700">{row.source.moduleLabel}</p>
                      <p className="mt-0.5 max-w-40 truncate font-mono text-xs text-slate-500">
                        {row.source.ref}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge>{row.branch.code}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3">
                      <PriorityBadge priority={row.priority} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.assignedTo ? row.assignedTo.name : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                      {formatWhen(row.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailId(row.id);
                        }}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={to >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* ---------------- detail modal ---------------- */}
      {detailId && (
        <Modal title={detail ? `Case ${detail.case.reference}` : 'Control review case'} onClose={() => setDetailId(null)}>
          {detailError && <ErrorBanner message={detailError} />}
          {actionError && <ErrorBanner message={actionError} />}
          {!detail && !detailError && (
            <div className="flex items-center justify-center gap-3 py-8 text-slate-500">
              <Spinner className="h-5 w-5 text-indigo-600" /> Loading case…
            </div>
          )}

          {detail && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={detail.case.status} />
                <PriorityBadge priority={detail.case.priority} />
                <Badge>{detail.case.branch.code}</Badge>
                <Badge tone="indigo">{detail.case.categoryLabel}</Badge>
                {detail.case.reopenCount > 0 && (
                  <Badge tone="amber">Reopened {detail.case.reopenCount}×</Badge>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-slate-900">{detail.case.title}</h3>
                {detail.case.description && (
                  <p className="mt-1 text-sm text-slate-600">{detail.case.description}</p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  Opened {formatWhen(detail.case.createdAt)} by {detail.case.createdBy?.name ?? 'Not recorded'}
                  {detail.case.assignedTo ? ` · reviewer: ${detail.case.assignedTo.name}` : ' · no reviewer assigned'}
                </p>
              </div>

              {/* Why it was flagged (snapshot, immutable) */}
              <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Icon name="alert" className="h-4 w-4 text-amber-500" />
                  Why this case was opened (snapshot)
                </h4>
                <p className="mt-1.5 text-sm text-slate-700">{detail.evidence.reason}</p>
                <p className="mt-1 text-xs text-slate-400">
                  Captured {formatWhen(detail.evidence.capturedAt)} — never rewritten afterwards.
                </p>
                {detail.evidence.indicators.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {detail.evidence.indicators.map((indicator) => (
                      <Badge key={indicator} tone="amber">
                        {indicator}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="mt-3 divide-y divide-slate-200/70">
                  {detail.evidence.facts.map((fact) => (
                    <FactRow key={fact.label} fact={fact} />
                  ))}
                </div>
                {detail.evidence.limitations.length > 0 && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-semibold text-amber-800">What the data does not show</p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-amber-800">
                      {detail.evidence.limitations.map((limitation) => (
                        <li key={limitation}>{limitation}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>

              {/* Live comparison (current state) */}
              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Icon name="clock" className="h-4 w-4 text-indigo-500" />
                  Current state (live from the source module)
                </h4>
                {!detail.current ? (
                  <p className="mt-1.5 text-sm text-slate-500">
                    The current state could not be derived.
                  </p>
                ) : !detail.current.available ? (
                  <p className="mt-1.5 text-sm text-rose-600">{detail.current.summary}</p>
                ) : (
                  <>
                    <p className="mt-1.5 text-sm text-slate-700">{detail.current.summary}</p>
                    {detail.current.changedSinceCapture ? (
                      <div className="mt-2 rounded-lg bg-amber-50 p-3">
                        <p className="text-xs font-semibold text-amber-800">
                          Changed since this case was opened
                        </p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-amber-800">
                          {detail.current.changedFacts.map((fact) => (
                            <li key={fact.label}>
                              {fact.label}: {fact.value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-slate-500">No change since the snapshot.</p>
                    )}
                    <Link
                      to={SOURCE_LINKS[detail.case.source.module]}
                      className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:underline"
                    >
                      Open {detail.case.source.ref} in {detail.case.source.moduleLabel}
                    </Link>
                  </>
                )}
              </section>

              {/* Resolution + reopen (kept visible forever) */}
              {detail.case.resolutionNotes && (
                <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <h4 className="text-sm font-semibold text-emerald-800">Resolution</h4>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm text-emerald-900">
                    {detail.case.resolutionNotes}
                  </p>
                  <p className="mt-1 text-xs text-emerald-700">
                    Recorded by {detail.case.resolvedBy?.name ?? 'Not recorded'}
                    {detail.case.resolvedAt ? ` · ${formatWhen(detail.case.resolvedAt)}` : ''}
                  </p>
                  {detail.case.reopenReason && detail.case.status !== 'RESOLVED' && (
                    <div className="mt-3 border-t border-emerald-200 pt-2">
                      <p className="text-xs font-semibold text-rose-700">
                        Reopened {detail.case.reopenedAt ? formatWhen(detail.case.reopenedAt) : ''} by{' '}
                        {detail.case.reopenedBy?.name ?? 'Not recorded'}
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-xs text-rose-700">
                        {detail.case.reopenReason}
                      </p>
                    </div>
                  )}
                </section>
              )}

              {/* Chronological history (facts + attributed notes) */}
              <section>
                <h4 className="text-sm font-semibold text-slate-800">History</h4>
                <ol className="mt-2 space-y-2">
                  {detail.history.map((entry) => (
                    <li key={entry.id} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800">
                          {HISTORY_LABELS[entry.action] ?? entry.action}
                        </span>
                        <span className="text-xs text-slate-400">{formatWhen(entry.createdAt)}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {entry.actor.name} ({entry.actorRole})
                      </p>
                      {entry.note && (
                        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{entry.note}</p>
                      )}
                    </li>
                  ))}
                </ol>
              </section>

              {/* Actions */}
              <section className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
                {canReview && (
                  <Button size="sm" disabled={actionBusy} onClick={() => void runAction('review')}>
                    Start review
                  </Button>
                )}
                {canAct && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={actionBusy}
                    onClick={() => {
                      setActionText('');
                      setActionOpen(true);
                    }}
                  >
                    Request corrective action
                  </Button>
                )}
                {canContinue && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={actionBusy}
                    onClick={() => void runAction('continue')}
                  >
                    Continue review
                  </Button>
                )}
                {canResolve && (
                  <Button
                    size="sm"
                    disabled={actionBusy}
                    onClick={() => {
                      setResolveText('');
                      setResolveOpen(true);
                    }}
                  >
                    Resolve
                  </Button>
                )}
                {canReopen && (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={actionBusy}
                    onClick={() => {
                      setReopenText('');
                      setReopenOpen(true);
                    }}
                  >
                    Reopen
                  </Button>
                )}
                {canNote && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={actionBusy}
                    onClick={() => {
                      setNoteText('');
                      setNoteOpen(true);
                    }}
                  >
                    Add note
                  </Button>
                )}
              </section>
            </div>
          )}
        </Modal>
      )}

      {/* ---------------- note dialog ---------------- */}
      {noteOpen && detail && (
        <Modal
          title="Add investigation note"
          onClose={() => setNoteOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setNoteOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={actionBusy || !noteText.trim()}
                onClick={async () => {
                  if (await runAction('note', { note: noteText.trim() })) setNoteOpen(false);
                }}
              >
                Save note
              </Button>
            </>
          }
        >
          <p className="mb-2 text-sm text-slate-500">
            Record what you observed or did — facts only. The note is attributed to you and kept
            forever in the case history.
          </p>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={4}
            maxLength={2000}
            className={`${inputCls} h-auto py-2.5`}
            placeholder="Checked the washing log for the shift; recount performed with a second person."
          />
        </Modal>
      )}

      {/* ---------------- corrective action dialog ---------------- */}
      {actionOpen && detail && (
        <Modal
          title="Record corrective action"
          onClose={() => setActionOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setActionOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={actionBusy || !actionText.trim()}
                onClick={async () => {
                  if (await runAction('action', { correctiveAction: actionText.trim() })) {
                    setActionOpen(false);
                  }
                }}
              >
                Record action
              </Button>
            </>
          }
        >
          <p className="mb-2 text-sm text-slate-500">
            Describe the next corrective step. The case moves to “Awaiting action” until it is
            performed.
          </p>
          <textarea
            value={actionText}
            onChange={(e) => setActionText(e.target.value)}
            rows={4}
            maxLength={2000}
            className={`${inputCls} h-auto py-2.5`}
            placeholder="Recount the folding table stock with a second person present."
          />
        </Modal>
      )}

      {/* ---------------- resolve dialog ---------------- */}
      {resolveOpen && detail && (
        <Modal
          title={`Resolve ${detail.case.reference}`}
          onClose={() => setResolveOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setResolveOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={actionBusy || !resolveText.trim()}
                onClick={async () => {
                  if (await runAction('resolve', { resolutionNotes: resolveText.trim() })) {
                    setResolveOpen(false);
                  }
                }}
              >
                Resolve case
              </Button>
            </>
          }
        >
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Resolving closes this review only. It never marks a transaction as paid, never changes a
            reconciliation, and never edits inventory, processing, handover or audit records — make
            any real correction in its own module, which records its own trail.
          </div>
          <p className="mb-2 text-sm text-slate-500">
            A written explanation is required and is kept verbatim, even if the case is reopened
            later.
          </p>
          <textarea
            value={resolveText}
            onChange={(e) => setResolveText(e.target.value)}
            rows={4}
            maxLength={4000}
            className={`${inputCls} h-auto py-2.5`}
            placeholder="Recount completed with two staff; one item was found on the folding table. No further gap recorded."
          />
        </Modal>
      )}

      {/* ---------------- reopen dialog ---------------- */}
      {reopenOpen && detail && (
        <Modal
          title={`Reopen ${detail.case.reference}`}
          onClose={() => setReopenOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setReopenOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={actionBusy || !reopenText.trim()}
                onClick={async () => {
                  if (await runAction('reopen', { reopenReason: reopenText.trim() })) {
                    setReopenOpen(false);
                  }
                }}
              >
                Reopen case
              </Button>
            </>
          }
        >
          <p className="mb-2 text-sm text-slate-500">
            The original resolution stays visible; the reopening is recorded separately with your
            name and reason.
          </p>
          <textarea
            value={reopenText}
            onChange={(e) => setReopenText(e.target.value)}
            rows={3}
            maxLength={2000}
            className={`${inputCls} h-auto py-2.5`}
            placeholder="Customer reported a missing shirt after resolution."
          />
        </Modal>
      )}

      {/* ---------------- open-case form ---------------- */}
      {form && (
        <Modal
          title="Open a control review case"
          onClose={() => setForm(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setForm(null)}>
                Cancel
              </Button>
              <Button
                disabled={formBusy || !form.title.trim() || !form.ref.trim()}
                onClick={() => void submitOpen()}
              >
                {formBusy ? 'Opening…' : 'Open case'}
              </Button>
            </>
          }
        >
          {formError && <ErrorBanner message={formError} />}
          <div className="space-y-4">
            <Field label="Category" hint={CONTROL_REVIEW_CATEGORY_HINTS[form.category]}>
              <select
                value={form.category}
                onChange={(e) => {
                  const category = e.target.value as ControlReviewCategory;
                  const module = CONTROL_REVIEW_CATEGORY_SOURCES[category][0];
                  setForm({ ...form, category, module });
                }}
                className={inputCls}
              >
                {CONTROL_REVIEW_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {CONTROL_REVIEW_CATEGORY_LABELS[category]}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Source record"
              hint={`The record this case reviews. Supported: ${CONTROL_REVIEW_CATEGORY_SOURCES[form.category]
                .map((module) => CONTROL_REVIEW_SOURCE_MODULE_LABELS[module])
                .join(' or ')}. The branch, priority and evidence are derived server-side from this record.`}
            >
              <div className="flex gap-2">
                {CONTROL_REVIEW_CATEGORY_SOURCES[form.category].length > 1 && (
                  <select
                    value={form.module}
                    onChange={(e) => setForm({ ...form, module: e.target.value as ControlReviewSourceModule })}
                    aria-label="Source module"
                    className={`${inputCls} w-40`}
                  >
                    {CONTROL_REVIEW_CATEGORY_SOURCES[form.category].map((module) => (
                      <option key={module} value={module}>
                        {CONTROL_REVIEW_SOURCE_MODULE_LABELS[module]}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  value={form.ref}
                  onChange={(e) => setForm({ ...form, ref: e.target.value })}
                  placeholder={
                    form.module === 'TRANSACTION'
                      ? 'Transaction number, e.g. LH1-000042'
                      : form.module === 'PURCHASE' || form.module === 'EXPENSE' || form.module === 'HANDOVER'
                        ? 'Document number, e.g. PUR-LH1-000001'
                        : 'Record id from the source page'
                  }
                  className={inputCls}
                />
              </div>
            </Field>

            <Field label="Title" hint="A short factual title — never an accusation.">
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={160}
                className={inputCls}
                placeholder="Count difference on the initial wash step"
              />
            </Field>

            <Field label="Description (optional)" hint="Context you want recorded with the case.">
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                maxLength={2000}
                className={`${inputCls} h-auto py-2.5`}
                placeholder="Customer reported a missing item after pickup."
              />
            </Field>

            <Field
              label="Initial note (optional)"
              hint="First investigation note, attributed to you and kept in the history."
            >
              <textarea
                value={form.initialNote}
                onChange={(e) => setForm({ ...form, initialNote: e.target.value })}
                rows={2}
                maxLength={2000}
                className={`${inputCls} h-auto py-2.5`}
              />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
